import React, { useState, useEffect } from 'react';
import {
    getGroupDetails, leaveGroup, deleteGroup, getAllUsers,
    addGroupMembers, removeGroupMember,
    promoteGroupAdmin, demoteGroupAdmin,
    blockGroupMember, unblockGroupMember,
    updateGroupInfo
} from '../../services/api';
import { Icons } from '../Icons/Icons';
import t from '../../i18n/useTranslation';
import useModalClose from '../../hooks/useModalClose';
import UserInfoModal from '../UserInfoModal/UserInfoModal';
import './GroupSettingsModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-dlmm.onrender.com/api';

// Etiquetas locales — i18n centralizado en translations.js cubre lo principal,
// pero estos nuevos botones (admin/bloqueo) merecen labels en idioma del usuario.
function getLang() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return u.language || 'español';
    } catch { return 'español'; }
}
const L10N = {
    español:  { participants:'Participantes', addTab:'Añadir', blockedTab:'Bloqueados', adminBadge:'Admin', creatorBadge:'Creador', makeAdmin:'Hacer admin', removeAdmin:'Quitar admin', blockUser:'Bloquear', unblock:'Desbloquear', noBlocked:'No hay usuarios bloqueados', actions:'Acciones', cancel:'Cancelar', confirmBlock:'Bloquear seleccionados', confirmPromote:'Nombrar admin', selectMembers:'Selecciona uno o más miembros', blockedNote:'Los bloqueados no pueden volver a este grupo a menos que los desbloquees.', edit:'Editar', editGroup:'Editar grupo', groupName:'Nombre del grupo', groupDesc:'Descripción', avatarLabel:'Foto del grupo', changePhoto:'Cambiar foto', save:'Guardar' },
    english:  { participants:'Members', addTab:'Add', blockedTab:'Blocked', adminBadge:'Admin', creatorBadge:'Creator', makeAdmin:'Make admin', removeAdmin:'Remove admin', blockUser:'Block', unblock:'Unblock', noBlocked:'No blocked users', actions:'Actions', cancel:'Cancel', confirmBlock:'Block selected', confirmPromote:'Promote', selectMembers:'Select one or more members', blockedNote:'Blocked users cannot rejoin this group until you unblock them.', edit:'Edit', editGroup:'Edit group', groupName:'Group name', groupDesc:'Description', avatarLabel:'Group photo', changePhoto:'Change photo', save:'Save' },
    euskera:  { participants:'Partaideak', addTab:'Gehitu', blockedTab:'Blokeatuak', adminBadge:'Admin', creatorBadge:'Sortzailea', makeAdmin:'Admin egin', removeAdmin:'Admin kendu', blockUser:'Blokeatu', unblock:'Desblokeatu', noBlocked:'Ez dago blokeatutako erabiltzailerik', actions:'Ekintzak', cancel:'Utzi', confirmBlock:'Blokeatu hautatutakoak', confirmPromote:'Admin izendatu', selectMembers:'Hautatu kide bat edo gehiago', blockedNote:'Blokeatutakoek ezin dute taldera itzuli desblokeatu arte.', edit:'Editatu', editGroup:'Taldea editatu', groupName:'Taldearen izena', groupDesc:'Deskribapena', avatarLabel:'Taldearen argazkia', changePhoto:'Argazkia aldatu', save:'Gorde' },
    français: { participants:'Membres', addTab:'Ajouter', blockedTab:'Bloqués', adminBadge:'Admin', creatorBadge:'Créateur', makeAdmin:'Nommer admin', removeAdmin:'Retirer admin', blockUser:'Bloquer', unblock:'Débloquer', noBlocked:'Aucun utilisateur bloqué', actions:'Actions', cancel:'Annuler', confirmBlock:'Bloquer la sélection', confirmPromote:'Nommer admin', selectMembers:'Sélectionnez un ou plusieurs membres', blockedNote:'Les utilisateurs bloqués ne peuvent revenir tant que vous ne les débloquez pas.', edit:'Modifier', editGroup:'Modifier le groupe', groupName:'Nom du groupe', groupDesc:'Description', avatarLabel:'Photo du groupe', changePhoto:'Changer la photo', save:'Enregistrer' },
    deutsch:  { participants:'Mitglieder', addTab:'Hinzufügen', blockedTab:'Blockiert', adminBadge:'Admin', creatorBadge:'Ersteller', makeAdmin:'Zum Admin machen', removeAdmin:'Admin entfernen', blockUser:'Blockieren', unblock:'Entsperren', noBlocked:'Keine blockierten Nutzer', actions:'Aktionen', cancel:'Abbrechen', confirmBlock:'Auswahl blockieren', confirmPromote:'Zum Admin', selectMembers:'Wähle ein oder mehrere Mitglieder', blockedNote:'Blockierte Nutzer können erst wieder beitreten, wenn du sie entsperrst.', edit:'Bearbeiten', editGroup:'Gruppe bearbeiten', groupName:'Gruppenname', groupDesc:'Beschreibung', avatarLabel:'Gruppenfoto', changePhoto:'Foto ändern', save:'Speichern' },
    italiano: { participants:'Membri', addTab:'Aggiungi', blockedTab:'Bloccati', adminBadge:'Admin', creatorBadge:'Creatore', makeAdmin:'Promuovi ad admin', removeAdmin:'Rimuovi admin', blockUser:'Blocca', unblock:'Sblocca', noBlocked:'Nessun utente bloccato', actions:'Azioni', cancel:'Annulla', confirmBlock:'Blocca selezionati', confirmPromote:'Nomina admin', selectMembers:'Seleziona uno o più membri', blockedNote:'Gli utenti bloccati non possono rientrare finché non li sblocchi.', edit:'Modifica', editGroup:'Modifica gruppo', groupName:'Nome del gruppo', groupDesc:'Descrizione', avatarLabel:'Foto del gruppo', changePhoto:'Cambia foto', save:'Salva' },
    português:{ participants:'Membros', addTab:'Adicionar', blockedTab:'Bloqueados', adminBadge:'Admin', creatorBadge:'Criador', makeAdmin:'Tornar admin', removeAdmin:'Remover admin', blockUser:'Bloquear', unblock:'Desbloquear', noBlocked:'Nenhum usuário bloqueado', actions:'Ações', cancel:'Cancelar', confirmBlock:'Bloquear selecionados', confirmPromote:'Promover', selectMembers:'Selecione um ou mais membros', blockedNote:'Os bloqueados não podem voltar até serem desbloqueados.', edit:'Editar', editGroup:'Editar grupo', groupName:'Nome do grupo', groupDesc:'Descrição', avatarLabel:'Foto do grupo', changePhoto:'Alterar foto', save:'Guardar' }
};
function tx() { return L10N[getLang()] || L10N.español; }

function GroupSettingsModal({ group, currentUser, onClose, onMembersUpdated, onGroupDeleted, initialTab }) {
    useModalClose(onClose);
    const [activeTab, setActiveTab] = useState(initialTab || 'members');
    const [fullGroup, setFullGroup] = useState(group);
    const [, setLoading] = useState(true);
    const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showTransferAdmin, setShowTransferAdmin] = useState(false);
    const [selectedNewAdmin, setSelectedNewAdmin] = useState(null);
    const [availableContacts, setAvailableContacts] = useState([]);
    const [selectedToAdd, setSelectedToAdd] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [groupAvatar, setGroupAvatar] = useState(group.avatar || '');

    // Modal nombrar admin (checkboxes)
    const [showPromoteModal, setShowPromoteModal] = useState(false);
    const [selectedToPromote, setSelectedToPromote] = useState([]);

    // Modal bloquear (checkboxes)
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [selectedToBlock, setSelectedToBlock] = useState([]);

    // Menú de acciones inline sobre un miembro (admin)
    const [actionMenuFor, setActionMenuFor] = useState(null); // memberId

    // Modal de edición del grupo (avatar + nombre + descripción)
    const [showEditModal, setShowEditModal] = useState(false);
    const [editName, setEditName] = useState(group.name || '');
    const [editDescription, setEditDescription] = useState(group.description || '');
    const [savingEdit, setSavingEdit] = useState(false);

    // Perfil de un miembro al pulsar su tarjeta
    const [profileMember, setProfileMember] = useState(null);

    const lx = tx();

    useEffect(() => {
        loadGroupDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 'add') loadAvailableContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleAvatarChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const formData = new FormData();
            formData.append('avatar', file);
            formData.append('groupId', fullGroup._id);
            formData.append('userId', currentUser._id);
            const response = await fetch(`${API_URL}/groups/avatar`, { method: 'PUT', body: formData });
            const data = await response.json();
            if (data.success) { setGroupAvatar(data.avatar); window.dispatchEvent(new Event('conversations_updated')); }
        } catch (err) { console.error('Error al cambiar avatar del grupo:', err); }
    };

    const loadGroupDetails = async () => {
        try {
            const response = await getGroupDetails(group.id || group._id);
            setFullGroup(response.data.group);
            setEditName(response.data.group?.name || '');
            setEditDescription(response.data.group?.description || '');
        } catch (error) { console.error('Error al cargar detalles del grupo:', error); }
        setLoading(false);
    };

    // Guardar cambios de nombre/descripción del grupo (avatar usa su propio
    // endpoint en handleAvatarChange).
    const handleSaveEdit = async () => {
        const trimmedName = (editName || '').trim();
        if (!trimmedName) { alert(lx.groupName); return; }
        setSavingEdit(true);
        try {
            await updateGroupInfo(fullGroup._id, {
                name: trimmedName,
                description: (editDescription || '').trim()
            });
            await loadGroupDetails();
            setShowEditModal(false);
            window.dispatchEvent(new Event('conversations_updated'));
        } catch (error) {
            alert(error?.response?.data?.error || 'Error');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleAddMembers = async () => {
        if (selectedToAdd.length === 0) { alert(t.group.error.noContacts); return; }
        try {
            await addGroupMembers({ groupId: fullGroup._id, memberIds: selectedToAdd, addedBy: currentUser.name });
            await loadGroupDetails();
            setSelectedToAdd([]);
            setActiveTab('members');
        } catch (error) {
            alert(error?.response?.data?.error || t.group.error.add);
        }
    };

    const handleRemoveMember = async (memberId, memberName) => {
        if ((fullGroup.creator?._id || fullGroup.creator) === memberId) { alert(t.group.error.removeCreator); return; }
        if (!window.confirm(`${memberName}?`)) return;
        try {
            await removeGroupMember({ groupId: fullGroup._id, memberId });
            await loadGroupDetails();
        } catch (error) { alert(t.group.error.remove); }
    };

    const handleToggleAdmin = async (memberId) => {
        try {
            const isAdminAlready = fullGroup.admins?.some(a => (a._id || a)?.toString() === memberId.toString());
            if (isAdminAlready) {
                await demoteGroupAdmin({ groupId: fullGroup._id, memberId });
            } else {
                await promoteGroupAdmin({ groupId: fullGroup._id, memberIds: [memberId] });
            }
            await loadGroupDetails();
            setActionMenuFor(null);
        } catch (error) {
            alert(error?.response?.data?.error || 'Error');
        }
    };

    const handleBlockSingle = async (memberId, memberName) => {
        if (!window.confirm(`${lx.blockUser} ${memberName}?`)) return;
        try {
            await blockGroupMember({ groupId: fullGroup._id, memberIds: [memberId] });
            await loadGroupDetails();
            setActionMenuFor(null);
        } catch (error) {
            alert(error?.response?.data?.error || 'Error');
        }
    };

    const handlePromoteBatch = async () => {
        if (selectedToPromote.length === 0) return;
        try {
            await promoteGroupAdmin({ groupId: fullGroup._id, memberIds: selectedToPromote });
            await loadGroupDetails();
            setShowPromoteModal(false);
            setSelectedToPromote([]);
        } catch (error) {
            alert(error?.response?.data?.error || 'Error');
        }
    };

    const handleBlockBatch = async () => {
        if (selectedToBlock.length === 0) return;
        try {
            await blockGroupMember({ groupId: fullGroup._id, memberIds: selectedToBlock });
            await loadGroupDetails();
            setShowBlockModal(false);
            setSelectedToBlock([]);
        } catch (error) {
            alert(error?.response?.data?.error || 'Error');
        }
    };

    const handleUnblock = async (memberId) => {
        try {
            await unblockGroupMember({ groupId: fullGroup._id, memberId });
            await loadGroupDetails();
        } catch (error) {
            alert(error?.response?.data?.error || 'Error');
        }
    };

    const toggleContactToAdd = (contactId) => {
        setSelectedToAdd(prev => prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]);
    };
    const toggleToPromote = (id) => {
        setSelectedToPromote(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const toggleToBlock = (id) => {
        setSelectedToBlock(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const loadAvailableContacts = async () => {
        setLoadingContacts(true);
        try {
            const response = await getAllUsers();
            const allUsers = response.data.users || [];
            const currentMemberIds = fullGroup.members.map(m => (m._id || m.id)?.toString());
            const blockedIds = (fullGroup.blockedUsers || []).map(b => (b._id || b)?.toString());
            const available = allUsers.filter(u =>
                !currentMemberIds.includes(u._id?.toString())
                && !blockedIds.includes(u._id?.toString())
                && u._id?.toString() !== currentUser._id?.toString()
            );
            setAvailableContacts(available);
        } catch (error) { console.error('Error al cargar contactos:', error); }
        setLoadingContacts(false);
    };

    const handleLeaveGroup = async () => {
        await loadGroupDetails();
        const otherAdmins = fullGroup.admins?.filter(admin => (admin._id || admin)?.toString() !== currentUser._id?.toString()) || [];
        const otherMembers = fullGroup.members?.filter(m => (m._id || m.id)?.toString() !== currentUser._id?.toString()) || [];
        // Si soy admin único Y quedan otros miembros: forzar transferencia o expulsión total
        if (isAdmin && otherAdmins.length === 0 && otherMembers.length > 0) {
            setShowTransferAdmin(true);
        } else {
            setShowLeaveConfirm(true);
        }
    };

    const handleConfirmLeave = async (newAdminId = null) => {
        try {
            await leaveGroup({ groupId: fullGroup._id, userId: currentUser._id, newAdminId: newAdminId || null });
            onMembersUpdated(); onClose();
        } catch (error) {
            alert(error?.response?.data?.error || t.group.error.leave);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteGroup({ groupId: fullGroup._id, userId: currentUser._id });
            onClose();
            if (onGroupDeleted) { onGroupDeleted(); } else { onMembersUpdated(); }
        } catch (error) { alert(t.group.error.delete); }
    };

    const isAdmin = fullGroup.admins?.some(admin => (admin._id || admin)?.toString() === currentUser._id?.toString());
    const creatorId = (fullGroup.creator?._id || fullGroup.creator)?.toString();

    // Candidatos para nombrar admin: miembros que NO son ya admin y NO soy yo
    const promoteCandidates = (fullGroup.members || []).filter(m => {
        const id = (m._id || m.id)?.toString();
        const isAlreadyAdmin = fullGroup.admins?.some(a => (a._id || a)?.toString() === id);
        return !isAlreadyAdmin && id !== currentUser._id?.toString();
    });

    // Candidatos para bloquear: miembros que NO son creador ni yo mismo
    const blockCandidates = (fullGroup.members || []).filter(m => {
        const id = (m._id || m.id)?.toString();
        return id !== creatorId && id !== currentUser._id?.toString();
    });

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content group-settings-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="group-avatar-container">
                        <div className="group-avatar-large">
                            {groupAvatar
                                ? <img src={groupAvatar} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                : fullGroup.name?.charAt(0).toUpperCase()
                            }
                        </div>
                    </div>
                    <h2>{fullGroup.name}</h2>
                    {fullGroup.description ? (
                        <div className="gsm-group-desc">
                            {fullGroup.description}
                        </div>
                    ) : null}
                    {isAdmin && (
                        <button
                            type="button"
                            className="gsm-edit-btn"
                            onClick={() => { setEditName(fullGroup.name || ''); setEditDescription(fullGroup.description || ''); setShowEditModal(true); }}
                        >
                            <Icons.Edit size={12} /> {lx.edit}
                        </button>
                    )}
                    <button className="close-button" onClick={onClose}>
                        <Icons.Close size={18} color="var(--text-secondary)" />
                    </button>
                </div>

                <div className="settings-tabs">
                    <button className={`settings-tab ${activeTab === 'members' ? 'active' : ''}`} onClick={() => setActiveTab('members')}>
                        <Icons.Group size={16} />
                        <span className="gsm-tab-label">{lx.participants}</span>
                        <span className="gsm-tab-count">({fullGroup.members?.length || 0})</span>
                    </button>
                    {isAdmin && (
                        <button className={`settings-tab ${activeTab === 'add' ? 'active' : ''}`} onClick={() => setActiveTab('add')}>
                            <Icons.Plus size={16} />
                            <span className="gsm-tab-label">{lx.addTab}</span>
                        </button>
                    )}
                    {isAdmin && (
                        <button className={`settings-tab ${activeTab === 'blocked' ? 'active' : ''}`} onClick={() => setActiveTab('blocked')}>
                            <Icons.Trash size={16} />
                            <span className="gsm-tab-label">{lx.blockedTab}</span>
                            <span className="gsm-tab-count">({(fullGroup.blockedUsers || []).length})</span>
                        </button>
                    )}
                </div>

                <div className="modal-body">
                    {activeTab === 'members' && (
                        <div className="members-list">
                            {/* Botones admin batch arriba */}
                            {isAdmin && (
                                <div className="gsm-admin-batch">
                                    <button
                                        type="button"
                                        className="gsm-batch-btn gsm-batch-promote"
                                        onClick={() => { setSelectedToPromote([]); setShowPromoteModal(true); }}
                                        disabled={promoteCandidates.length === 0}
                                    >
                                        <Icons.Star size={14} /> {lx.makeAdmin}
                                    </button>
                                    <button
                                        type="button"
                                        className="gsm-batch-btn gsm-batch-block"
                                        onClick={() => { setSelectedToBlock([]); setShowBlockModal(true); }}
                                        disabled={blockCandidates.length === 0}
                                    >
                                        <Icons.Trash size={14} /> {lx.blockUser}
                                    </button>
                                </div>
                            )}
                            {fullGroup.members?.map(member => {
                                const memberId = (member._id || member.id)?.toString();
                                const memberIsAdmin = fullGroup.admins?.some(admin => (admin._id || admin)?.toString() === memberId);
                                const memberIsCreator = creatorId === memberId;
                                const isSelf = memberId === currentUser._id?.toString();
                                const canManage = isAdmin && !memberIsCreator && !isSelf;
                                return (
                                    <div key={memberId} className="member-item" style={{ position: 'relative' }}>
                                        {/* La zona avatar + nombre + idioma es clicable y
                                            abre el perfil Kaixo del miembro. El botón
                                            ⋯ (canManage) sigue siendo independiente para
                                            evitar conflictos de eventos. */}
                                        <div
                                            onClick={() => setProfileMember(member)}
                                            style={{ display: 'flex', alignItems: 'center', flex: 1, cursor: 'pointer' }}
                                        >
                                            <div className="member-avatar">
                                                {member.avatar
                                                    ? <img src={member.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                    : (member.name?.charAt(0) || '?')}
                                            </div>
                                            <div className="member-info">
                                                <div className="member-name">
                                                    {member.name}
                                                    {memberIsCreator && <span className="badge creator"><Icons.Crown size={12} color="white" /> {lx.creatorBadge}</span>}
                                                    {memberIsAdmin && !memberIsCreator && <span className="badge admin"><Icons.Star size={12} color="white" /> {lx.adminBadge}</span>}
                                                </div>
                                                <div className="member-language">{member.language} - {member.region}</div>
                                            </div>
                                        </div>
                                        {canManage && (
                                            <button
                                                type="button"
                                                className="remove-member-btn"
                                                onClick={() => setActionMenuFor(actionMenuFor === memberId ? null : memberId)}
                                                aria-label={lx.actions}
                                                title={lx.actions}
                                            >
                                                <Icons.More size={18} color="#666" />
                                            </button>
                                        )}
                                        {canManage && actionMenuFor === memberId && (
                                            <div className="gsm-action-menu">
                                                <button type="button" className="gsm-action-item"
                                                    onClick={() => handleToggleAdmin(memberId)}>
                                                    {memberIsAdmin ? lx.removeAdmin : lx.makeAdmin}
                                                </button>
                                                <button type="button" className="gsm-action-item gsm-action-danger"
                                                    onClick={() => handleRemoveMember(memberId, member.name)}>
                                                    {t.group.removeMember || 'Eliminar'}
                                                </button>
                                                <button type="button" className="gsm-action-item gsm-action-danger"
                                                    onClick={() => handleBlockSingle(memberId, member.name)}>
                                                    {lx.blockUser}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {activeTab === 'add' && isAdmin && (
                        <div className="add-members-section">
                            {loadingContacts ? (
                                <div className="loading-contacts">{t.group.loading}</div>
                            ) : availableContacts.length === 0 ? (
                                <div className="no-contacts">{t.group.noContacts}</div>
                            ) : (
                                <>
                                    <div className="add-members-header">{selectedToAdd.length} {t.group.selected}</div>
                                    <div className="contacts-list">
                                        {availableContacts.map(user => (
                                            <div key={user._id} className={`contact-item ${selectedToAdd.includes(user._id) ? 'selected' : ''}`} onClick={() => toggleContactToAdd(user._id)}>
                                                <div className="member-avatar">
                                                    {user.avatar
                                                        ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                        : (user.name?.charAt(0) || '?')}
                                                </div>
                                                <div className="member-info">
                                                    <div className="member-name">{user.name}</div>
                                                    <div className="member-language">{user.language} - {user.region}</div>
                                                </div>
                                                <div className="contact-checkbox">
                                                    {selectedToAdd.includes(user._id) && <Icons.Check size={12} color="white" />}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="add-members-footer">
                                        <button className="add-members-btn" disabled={selectedToAdd.length === 0} onClick={handleAddMembers}>
                                            {t.group.addBtn} {selectedToAdd.length > 0 ? `(${selectedToAdd.length})` : ''}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'blocked' && isAdmin && (
                        <div className="members-list">
                            <div className="gsm-blocked-note">
                                {lx.blockedNote}
                            </div>
                            {(fullGroup.blockedUsers || []).length === 0 ? (
                                <div className="no-contacts">{lx.noBlocked}</div>
                            ) : (
                                fullGroup.blockedUsers.map(b => {
                                    const id = (b._id || b)?.toString();
                                    return (
                                        <div key={id} className="member-item">
                                            <div className="member-avatar">
                                                {b.avatar
                                                    ? <img src={b.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                                    : (b.name || '?').charAt(0)}
                                            </div>
                                            <div className="member-info">
                                                <div className="member-name">{b.name || id}</div>
                                            </div>
                                            <button type="button" className="gsm-unblock-btn" onClick={() => handleUnblock(id)}>
                                                {lx.unblock}
                                            </button>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="leave-group-btn" onClick={handleLeaveGroup}>
                        <Icons.DoorExit size={18} color="white" /> {t.group.leave.replace('🚪 ', '')}
                    </button>
                    {isAdmin && (
                        <button className="delete-group-btn" onClick={() => setShowDeleteConfirm(true)}>
                            <Icons.Trash size={18} color="white" /> {t.group.delete.replace('🗑️ ', '')}
                        </button>
                    )}
                </div>

                {/* Modal nombrar admin (checkboxes) */}
                {showPromoteModal && (
                    <div className="confirm-overlay" onClick={() => setShowPromoteModal(false)}>
                        <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
                            <h3>{lx.makeAdmin}</h3>
                            <p>{lx.selectMembers}</p>
                            <div className="transfer-admin-list">
                                {promoteCandidates.length === 0 ? (
                                    <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>—</div>
                                ) : promoteCandidates.map(member => {
                                    const id = (member._id || member.id)?.toString();
                                    const checked = selectedToPromote.includes(id);
                                    return (
                                        <div key={id} className={`transfer-member-item ${checked ? 'selected' : ''}`} onClick={() => toggleToPromote(id)}>
                                            <div className="member-avatar-small">{member.avatar ? <img src={member.avatar} alt="" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }} /> : (member.name?.charAt(0) || '?')}</div>
                                            <div className="member-name-small">{member.name}</div>
                                            <div className="transfer-checkbox">
                                                {checked && <Icons.Check size={12} color="white" />}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="confirm-buttons">
                                <button onClick={() => setShowPromoteModal(false)}>{lx.cancel}</button>
                                <button className="primary" disabled={selectedToPromote.length === 0} onClick={handlePromoteBatch}>
                                    {lx.confirmPromote} {selectedToPromote.length > 0 ? `(${selectedToPromote.length})` : ''}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal bloquear (checkboxes) */}
                {showBlockModal && (
                    <div className="confirm-overlay" onClick={() => setShowBlockModal(false)}>
                        <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
                            <h3>{lx.blockUser}</h3>
                            <p>{lx.blockedNote}</p>
                            <div className="transfer-admin-list">
                                {blockCandidates.length === 0 ? (
                                    <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>—</div>
                                ) : blockCandidates.map(member => {
                                    const id = (member._id || member.id)?.toString();
                                    const checked = selectedToBlock.includes(id);
                                    return (
                                        <div key={id} className={`transfer-member-item ${checked ? 'selected' : ''}`} onClick={() => toggleToBlock(id)}>
                                            <div className="member-avatar-small">{member.avatar ? <img src={member.avatar} alt="" style={{ width:'100%', height:'100%', borderRadius:'50%', objectFit:'cover' }} /> : (member.name?.charAt(0) || '?')}</div>
                                            <div className="member-name-small">{member.name}</div>
                                            <div className="transfer-checkbox">
                                                {checked && <Icons.Check size={12} color="white" />}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="confirm-buttons">
                                <button onClick={() => setShowBlockModal(false)}>{lx.cancel}</button>
                                <button className="danger" disabled={selectedToBlock.length === 0} onClick={handleBlockBatch}>
                                    {lx.confirmBlock} {selectedToBlock.length > 0 ? `(${selectedToBlock.length})` : ''}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showTransferAdmin && (
                    <div className="confirm-overlay">
                        <div className="confirm-dialog">
                            <h3>{t.group.transferAdmin}</h3>
                            <p>{t.group.transferDesc}</p>
                            <div className="transfer-admin-list">
                                {fullGroup.members?.filter(m => (m._id || m.id)?.toString() !== currentUser._id?.toString()).map(member => (
                                    <div key={member._id || member.id} className={`transfer-member-item ${selectedNewAdmin === (member._id || member.id) ? 'selected' : ''}`} onClick={() => setSelectedNewAdmin(member._id || member.id)}>
                                        <div className="member-avatar-small">{member.name?.charAt(0)}</div>
                                        <div className="member-name-small">{member.name}</div>
                                        <div className="transfer-checkbox">
                                            {selectedNewAdmin === (member._id || member.id) && <Icons.Check size={12} color="white" />}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="confirm-buttons">
                                <button onClick={() => setShowTransferAdmin(false)}>{t.group.cancel}</button>
                                <button className="primary" disabled={!selectedNewAdmin} onClick={() => { setShowTransferAdmin(false); handleConfirmLeave(selectedNewAdmin); }}>
                                    {t.group.transferAndLeave}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showLeaveConfirm && (
                    <div className="confirm-overlay">
                        <div className="confirm-dialog">
                            <h3>{t.group.leaveConfirm}</h3>
                            <p>{t.group.leaveWarning}</p>
                            <div className="confirm-buttons">
                                <button onClick={() => setShowLeaveConfirm(false)}>{t.group.cancel}</button>
                                <button className="danger" onClick={() => { setShowLeaveConfirm(false); handleConfirmLeave(); }}>
                                    {t.group.danger}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showDeleteConfirm && (
                    <div className="confirm-overlay">
                        <div className="confirm-dialog">
                            <h3>{fullGroup.name}</h3>
                            <p className="warning-text">{t.group.deleteConfirm}</p>
                            <p>{t.group.deleteConfirmText}</p>
                            <div className="confirm-buttons">
                                <button onClick={() => setShowDeleteConfirm(false)}>{t.group.cancel}</button>
                                <button className="danger" onClick={() => { setShowDeleteConfirm(false); handleConfirmDelete(); }}>
                                    {t.group.yesDelete}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de edición del grupo: avatar + nombre + descripción */}
                {showEditModal && (
                    <div className="confirm-overlay" onClick={() => setShowEditModal(false)}>
                        <div className="confirm-dialog gsm-edit-dialog" onClick={(e) => e.stopPropagation()}>
                            <h3>{lx.editGroup}</h3>

                            {/* Avatar (con file picker) */}
                            <div className="gsm-edit-avatar-wrap">
                                <div className="group-avatar-large">
                                    {groupAvatar
                                        ? <img src={groupAvatar} alt="avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                        : (fullGroup.name?.charAt(0).toUpperCase() || '?')}
                                </div>
                                <label className="gsm-change-photo-btn">
                                    <Icons.Camera size={12} color="white" /> {lx.changePhoto}
                                    <input type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
                                </label>
                            </div>

                            {/* Nombre */}
                            <label className="gsm-edit-label">{lx.groupName}</label>
                            <input
                                type="text"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                maxLength={100}
                                className="confirm-input"
                                placeholder={lx.groupName}
                            />

                            {/* Descripción */}
                            <label className="gsm-edit-label">{lx.groupDesc}</label>
                            <textarea
                                value={editDescription}
                                onChange={e => setEditDescription(e.target.value)}
                                maxLength={500}
                                rows={3}
                                className="confirm-input gsm-edit-textarea"
                                placeholder={lx.groupDesc}
                            />

                            <div className="confirm-buttons">
                                <button onClick={() => setShowEditModal(false)} disabled={savingEdit}>{lx.cancel}</button>
                                <button className="primary" onClick={handleSaveEdit} disabled={savingEdit || !(editName || '').trim()}>
                                    {savingEdit ? '…' : lx.save}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Perfil Kaixo del miembro al pulsar su tarjeta */}
                {profileMember && (
                    <UserInfoModal
                        user={profileMember}
                        currentUserLanguage={currentUser?.language || 'español'}
                        onClose={() => setProfileMember(null)}
                    />
                )}
            </div>
        </div>
    );
}

export default GroupSettingsModal;
